/**
 * UserMenu — header dropdown showing the current user's email and a Log out
 * action when authenticated, OR a "Log in" button when not.
 *
 * Hidden entirely if Supabase is not configured (parallel mode for users
 * who haven't set up the backend yet).
 */

import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, LogOut, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useAuthStore } from '@/store/useAuthStore';
import { signOut } from '@/lib/auth';
import { isSupabaseConfigured } from '@/lib/supabase';
import { toast } from '@/store/useToastStore';

export function UserMenu() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const isLoading = useAuthStore((s) => s.isLoading);
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // If Supabase isn't configured, hide auth UI entirely (parallel mode)
  if (!isSupabaseConfigured) return null;

  // Still resolving initial session
  if (isLoading) {
    return (
      <div className="ml-2 w-8 h-8 rounded-full bg-surface-muted animate-pulse" aria-hidden />
    );
  }

  // Not logged in → show Log in button
  if (!user) {
    return (
      <Button variant="outline" size="sm" onClick={() => navigate('/login')} className="ml-2">
        Log in
      </Button>
    );
  }

  // Logged in → show menu
  const initials = user.email
    ? user.email
        .split('@')[0]
        .split(/[._-]/)
        .slice(0, 2)
        .map((s) => s[0]?.toUpperCase() ?? '')
        .join('')
    : '?';

  const handleLogout = async () => {
    setOpen(false);
    const { error } = await signOut();
    if (error) {
      toast.error(`Could not log out: ${error}`);
      return;
    }
    toast.success('Logged out');
    navigate('/login');
  };

  return (
    <div ref={containerRef} className="ml-2 relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1 pr-2 pl-1 py-1 rounded-full hover:bg-surface-muted transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-orange"
        aria-label="Account menu"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <span className="w-8 h-8 rounded-full bg-brand-navy text-white flex items-center justify-center text-xs font-bold">
          {initials || <User className="w-4 h-4" />}
        </span>
        <ChevronDown
          className={`w-3 h-3 text-ink-tertiary transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full mt-1 w-64 bg-white border border-line rounded-md shadow-lg py-1 z-[110] animate-fade-in"
        >
          <div className="px-3 py-2 border-b border-line-light">
            <div className="text-xs text-ink-tertiary">Signed in as</div>
            <div className="text-sm font-semibold text-ink-primary truncate">{user.email}</div>
          </div>
          <button
            type="button"
            onClick={handleLogout}
            role="menuitem"
            className="w-full text-left px-3 py-2 text-sm text-ink-primary hover:bg-surface-muted transition-colors flex items-center gap-2"
          >
            <LogOut className="w-4 h-4 text-ink-tertiary" />
            Log out
          </button>
        </div>
      )}
    </div>
  );
}
